<template>
    <form-fields>
        <toolbar-buttons>
            <text-entry-button
                :placeholder="$t('V3_EDIT_MONOGRAM_PLACEHOLDER')"
                v-model.trim="selectedText"
                @input="updateText"
                :hint="$t('V3_EDIT_MONOGRAM_HINT')"
            />
        </toolbar-buttons>

        <step-buttons
            :class="{
                'cz3__hide-lg': buttonsSm,
            }"
        >
            <secondary-button
                class="cz3__flex-1"
                :label="$t('V3_BACK')"
                @click="back"
            />

            <action-button
                class="cz3__flex-1"
                :label="$t('V3_NEXT')"
                :disabled="!textEntered"
                @click="next"
            />
        </step-buttons>
    </form-fields>
</template>

<style lang="scss">
</style>

<script>
    import ToolbarButtons from './ToolbarButtons.vue';
    import FormFields from './FormFields.vue';
    import ActionButton from './ActionButton.vue';
    import SecondaryButton from './SecondaryButton.vue';
    import StepButtons from './StepButtons.vue';
    import TextEntryButton from './TextEntryButton.vue';

    import * as constants from '../../constants';

    // eslint-disable-next-line no-unused-vars
    import designArea from '../../utilities/designArea';
    // eslint-disable-next-line no-unused-vars
    import extent from '../../utilities/extent';

    export default {
        components: {
            ToolbarButtons,
            FormFields,
            ActionButton,
            SecondaryButton,
            StepButtons,
            TextEntryButton,
        },

        props: {
            buttonsSm: Boolean,
        },

        data() {
            return {
                selectedText: '',
            };
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            currentView() {
                return this.$store.state.customizer.currentView;
            },

            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            editPlacement() {
                if (this.editPlacements && this.editPlacements.length > 0) {
                    return this.editPlacements[0];
                }

                return null;
            },

            editComponent() {
                if (this.editPlacement) {
                    return this.viewController.selectedAtPlacement(this.editPlacement.code);
                }

                return null;
            },

            filledTextPlacements() {
                return this.$store.getters.filledTextPlacements;
            },

            emptyTextPlacements() {
                return this.$store.getters.emptyTextPlacements;
            },

            /**
             * Gets a list of filled images.
             */
            filledMonogramPlacements() {
                return this.$store.getters.filledMonogramPlacements;
            },

            filledPlacements() {
                return this.$store.getters.filledPlacements;
            },

            /**
             * Gets a list of free images.
             */
            emptyMonogramPlacements() {
                return this.$store.getters.emptyMonogramPlacements;
            },

            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            nextStep() {
                return this.$store.getters.nextStepV3;
            },

            textEntered() {
                return this.selectedText?.length > 0;
            },

            /**
             * Secondary bottles array.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },

            /**
             * Figure out a list of available styles.
             */
            availableStyles() {
                let placement = this.editPlacement;

                if (placement == null) {
                    if (this.emptyMonogramPlacements.length > 0) {
                        placement = this.emptyMonogramPlacements[0].placement;
                    }
                }

                if (placement) {
                    const fonts = [];

                    placement.components.forEach((c) => {
                        const font = c.description.find((d) => d.definition === constants.DEFINITION_MONOGRAM);

                        if (font) {
                            if (!fonts.some((f) => f.code === font.code)) {
                                const copy = { ...font };

                                if (copy.custom == null) {
                                    copy.custom = {};
                                }

                                copy.custom = { ...copy.custom, ...c.custom };

                                fonts.push(copy);
                            }
                        }
                    });

                    return fonts;
                }

                return [];
            },
        },

        mounted() {
            if (this.editPlacement == null) {
                this.addText();
            } else {
                this.loadFromPlacement();
            }
        },

        methods: {
            back() {
                this.$store.dispatch('setSelectedSubStepV3', 'choose');
            },

            /**
             * Update personalization.
             */
            updateText() {
                this.viewController.updatePersonalization(this.editPlacement.code, this.selectedText).then((result) => {
                    if (result.accepted) {
                        if (result.as) {
                            this.selectedText = result.as;
                        }
                    } else if (result.lastValid != null) {
                        this.selectedText = result.lastValid;
                    }
                });
            },

            next() {
                this.$store.dispatch('setSelectedSubStepV3', 'monogram-font');
            },

            addText() {
                if (this.emptyMonogramPlacements.length > 0 && this.availableStyles.length > 0) {
                    this.$store.dispatch('validateEditableViews');
                    this.$store.dispatch('switchToCommonView');

                    const { placement } = this.emptyMonogramPlacements[0];

                    this.$store.dispatch('takeEmptyPlacement', placement);

                    const startWithStyle = this.availableStyles[0];

                    this.viewController.updateSimilarComponent(placement.code, constants.DEFINITION_MONOGRAM, startWithStyle.code);

                    const component = this.viewController.selectedAtPlacement(placement.code);

                    // ... and center manually in the current view.
                    if (this.currentView) {
                        const { view } = this.currentView;

                        this.$store.dispatch('renderOnlyOnView', {
                            view: this.currentView,
                        });

                        if (view.custom) {
                            const {
                                left, right, top, bottom,
                            } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                            const width = +component.custom['personalization-width'];
                            const height = +component.custom['personalization-height'];

                            if (left != null && right != null && top != null && bottom != null) {
                                let yOffset = null;
                                let yOffsetMin = null;

                                let fontColor = null;

                                this.filledPlacements.forEach((p) => {
                                    if (this.personalizationMethod === 'printing') {
                                        return;
                                    }

                                    if (p.component.custom['personalization-y'] != null) {
                                        const below = +p.component.custom['personalization-y'] + (+p.component.custom['personalization-height']) * 1.5;

                                        if (yOffset == null) {
                                            yOffset = below;
                                        } else {
                                            yOffset = Math.max(below, yOffset);
                                        }

                                        const above = +p.component.custom['personalization-y'] - (+p.component.custom['personalization-height']) * 0.5;

                                        if (yOffsetMin == null) {
                                            yOffsetMin = above;
                                        } else {
                                            yOffsetMin = Math.min(above, yOffsetMin);
                                        }
                                    }

                                    if (p.component.custom['dynamic-image-height'] != null) {
                                        const y = +p.component.custom['dynamic-image-offset-y'];
                                        const h = +p.component.custom['dynamic-image-height'];
                                        const s = +p.component.custom['dynamic-image-scale'];
                                        const totalHeight = +p.component.custom['dynamic-image-area-height'];

                                        const below = y + (h * s * 0.5) + (totalHeight * 0.1);

                                        if (yOffset == null) {
                                            yOffset = below;
                                        } else {
                                            yOffset = Math.max(below, yOffset);
                                        }

                                        const above = y - (h * s * 0.5) - (totalHeight * 0.1);

                                        if (yOffsetMin == null) {
                                            yOffsetMin = above;
                                        } else {
                                            yOffsetMin = Math.min(above, yOffsetMin);
                                        }
                                    }

                                    if (p.component.custom['personalization-color'] != null) {
                                        fontColor = p.component.custom['personalization-color'];
                                    }
                                });

                                if (yOffset == null) {
                                    if ((this.viewController.blueprint.custom['v-default-alignment-v3'] || '').toLowerCase() === 'top') {
                                        yOffset = top + ((+(component.custom['personalization-size'])) * 0.5) + 10;
                                    } else {
                                        yOffset = (((top + bottom) * 0.5) - (height * 0.5));
                                    }
                                } else if (component.custom['personalization-size']) {
                                    const size = +component.custom['personalization-size'];

                                    if (yOffset + size >= bottom) {
                                        if (yOffsetMin < top + size * 0.25) {
                                            yOffset = (((top + bottom) * 0.5) - (height * 0.5));
                                        } else {
                                            yOffset = yOffsetMin - (size * 0.5);
                                        }
                                    } else {
                                        yOffset += size * 0.5;
                                    }
                                }

                                this.viewController.commitCustomAttributes(placement.code, {
                                    'v-design-on': view.custom['v-design-area'],
                                    mask: view.custom['v-design-mask'],
                                    'personalization-x': ((left + right) * 0.5) - (width * 0.5),
                                    'personalization-y': yOffset, // ((top + bottom) * 0.5) - (height * 0.5),
                                    'personalization-filled': 'true',
                                    'personalization-rotate': 0,
                                    'personalization-scale': 1,
                                }, {
                                    propagate: true,
                                });

                                const newSettings = {};

                                if (fontColor) {
                                    newSettings['personalization-color'] = fontColor;
                                }

                                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                                this.$store.dispatch('setGroup', { placement: placement.code, group });

                                this.viewController.commitCustomAttributes(placement.code, newSettings, {
                                    propagate: true,
                                });
                            }
                        }
                    }

                    this.beginEdit(placement);
                }
            },

            /**
             * Enters text editing mode.
             */
            beginEdit(placement) {
                this.$store.dispatch('lockSwipe', true);
                this.$store.dispatch('editPlacement', placement);

                this.loadFromPlacement();
            },

            loadFromPlacement() {
                this.selectedText = this.editComponent.custom['personalization-text'] || '';

                if (this.editComponent.custom['v-design-on']) {
                    this.$store.dispatch('switchToDesignView', this.editComponent.custom['v-design-on']);
                }
            },
        },
    };
</script>
